import { AscColorTagTypes } from '../common-types';
import { Module } from '../module';
import { SkillBadge } from '../skill';
import { Caption } from '../utility-types';

export interface Stage<M = Module[] | number> {
  id: string;
  title: string;
  modules: M;
  percentComplete?: number;
  completedModules?: number;
  totalModules?: number;
  averageGradePercentage?: number;
  badges?: SkillBadge[];
  groupTitle?: string;
  color?: string;
}

export type Bullpen = {
  id: string;
  name: string;
  date: string;
  startTime: string;
  endTime: string;
  createdAt: string;
  time: string;
  end: string;
  image: string;
  coins: number;
  points: number;
  thumbnail: {
    location: string;
  };
  video?: {
    location: string;
  };
  isApplied: boolean;
};
export type IBullpen = {
  event: {
    id: string;
    name: string;
    createdAt: string;
    updatedAt: string;
    date: string;
    time: string;
    startTime: string;
    endTime: string;
    end: string;
    image: string;
    description: string;
    coins: number;
    points: number;
    thumbnail: {
      location: string;
    };
    video?: {
      location: string;
      captions?: Caption[];
    };
  };
  isPast: boolean;
  isApplied: boolean;
};
export type PastWinner = {
  application: {
    address: string;
    birthDate: string;
    city: string;
    country: string;
    description: string;
    email: string;
    createdAt: string;
    event: {
      name: string;
    };
    eventId: string;
    id: string;
    name: string;
    phone: string;
    projectName: string;
    school: string;
    status: string;
    student: {
      user: {
        profilePicture: {
          location: string;
        };
      };
    };
  };
  applicationId: string;
  createdAt: string;
  id: string;
  rank: number;
  flag: string;
  score: number;
  updatedAt: string;
};

export type JobBoardCompany = {
  id: string;
  title: string;
  companySize: string;
  tags: string[];

  company: {
    title: string;
    city: string;
    country: string;
    createdAt: string;
    description: string;
    employees: string;
    id: string;
    isRemote: boolean;
    link: string;
    logo?: {
      bucket: string;
      id: string;
      key: string;
      location: string;
    };
    tags?: { tag: string; color: string; type?: AscColorTagTypes }[];
  };
  appliedVacancies?: {
    id: string;
    job: {
      company: {
        id: string;
        title: string;
        logo: {
          location: string;
        };
      };
      jobCategory: string;
    };
  }[];
  vacancies: {
    id: string;
    jobId: string;
    title: string;
    createdAt: string;
    isApplied: boolean;
    isFavorites: boolean;
    country: string;
    city: string;
    jobCategory: string;
    salary: string;
    maxSalary: number;
    minSalary: number;
    posted: string;
    liked: boolean;
    jobLocationType: 'remote';
  }[];
  jobCategories: string[];
};

export type JobBoardJob = {
  vacancy: {
    id: string;
    createdAt: string;
    updatedAt: string;
    companyId: string;
    title: string;
    description: string;
    jobCategory: string;
    minSalary: number;
    maxSalary: number;
    salary: null | number;
    location: null | string;
    country: string;
    city: string;
    isRemote: boolean;
    company: {
      id: string;
      createdAt: string;
      updatedAt: string;
      title: string;
      location: null | string;
      city: null | string;
      country: null | string;
      isRemote: null | boolean;
      employees: string;
      description: string;
      link: string;
      logo?: {
        id: string;
        createdAt: string;
        updatedAt: string;
        key: string;
        bucket: string;
        location: string;
        companyId: string;
      };
    };
  };
  isApplied: boolean;
  isFavorite: boolean;
};

export type JobBoardApplication = {
  job: {
    id: string;
    title: string;
    description: string;
    jobCategory: string;
    country: string;
    city: string;
    minSalary: number;
    maxSalary: number;
    companyId: string;
    createdAt: string;
    updatedAt: string;
  };
  id: string;
  posted: string;
  isApplied: boolean;
  isFavorite: boolean;
  liked: boolean;
  jobLocationType: 'remote';
  jobId: string;
  salary: string;
  createdAt: string;
  updatedAt: string;
  studentId: string;
  keyResponsibilities: string;
  mainRequirements: string;
  whatDoWeOffer: string;
}[];

export interface Company {
  id: string;
  createdAt: string;
  updatedAt: string;
  title: string;
  city: string;
  country: string;
  employees: string;
  jobLocationType: 'remote';
  description: string;
  link: string;
  logo?: {
    location: string;
    key: string;
    bucket: string;
    id: string;
  };
  tags?: { tag: string; type?: AscColorTagTypes }[];
  _count: {
    vacancies: number;
  };
}

export interface FilteredOptions {
  companies: {
    _count: {
      employees: number;
    };
    employees: string;
  }[];
  industries: {
    tagId: string;
    _count: number;
    name: string;
  }[];
  countries: [];
}

const StageType = {
  INTERNSHIP: 'internship',
  ENTREPRENEURSHIP: 'entrepreneurship',
} as const;

export type StageType = (typeof StageType)[keyof typeof StageType];
